/**
 * LegEditor — редактор ноги позиции (дизайн «Aikido Invest», ТЗ §3).
 * Презентационный слой: поля/типы данных и BS-греки не меняются, только стилизация.
 * Депозит — такая же «нога», как опцион (deposit-as-leg).
 */

import { useState } from "react";
import { computeGreeks, daysToExpiry } from "../lib/blackScholes";
import { Tip } from "./Tooltip";

export interface LegRow {
  type: "option" | "future" | "deposit" | "bond" | "stock" | "cash";
  name: string;
  quantity: number;
  _internal_id?: string;
  [k: string]: any;
}

export function blankLeg(type: LegRow["type"], underlying: string): LegRow {
  const today = new Date().toISOString().slice(0, 10);
  const oneYear = new Date(); oneYear.setFullYear(oneYear.getFullYear() + 1);
  const oneYearStr = oneYear.toISOString().slice(0, 10);

  switch (type) {
    case "option":
      return { type: "option", name: `${underlying} Call`, quantity: 1, side: "call", underlying, strike: 100, expiry: oneYearStr, premium_paid: 2.5, contract_multiplier: 100, _internal_id: Math.random().toString(36).slice(2) };
    case "future":
      return { type: "future", name: `${underlying} fut`, quantity: 1, underlying, entry_price: 100, contract_multiplier: 100, _internal_id: Math.random().toString(36).slice(2) };
    case "deposit":
      return { type: "deposit", name: "Депозит 5%", quantity: 1, principal: 100000, annual_rate: 0.05, entry_date: today, compounding: "daily", _internal_id: Math.random().toString(36).slice(2) };
    case "bond":
      return { type: "bond", name: "Облигация", quantity: 1, face_value: 1000, coupon_rate: 0.05, coupon_freq: 2, issue_date: today, maturity_date: oneYearStr, purchase_price: 1000, ytm: 0.05, _internal_id: Math.random().toString(36).slice(2) };
    case "stock":
      return { type: "stock", name: `${underlying} акции`, quantity: 100, underlying, entry_price: 100, entry_date: today, dividend_schedule: [], _internal_id: Math.random().toString(36).slice(2) };
    case "cash":
      return { type: "cash", name: "Кэш USD", quantity: 1000, _internal_id: Math.random().toString(36).slice(2) };
  }
}

const TYPE_LABELS: Record<LegRow["type"], string> = {
  option: "Опцион", future: "Фьючерс", deposit: "Депозит", bond: "Облигация", stock: "Акция", cash: "Кэш",
};

function swatchColor(leg: LegRow): string {
  if (leg.type === "option") return leg.side === "put" ? "var(--put)" : "var(--call)";
  if (leg.type === "deposit" || leg.type === "bond" || leg.type === "cash") return "var(--depo)";
  return "var(--txt-3)";
}

export function LegEditor({ leg, onChange, onRemove, marketSpot, marketIv, marketRisk }: {
  leg: LegRow; onChange: (l: LegRow) => void; onRemove: () => void;
  marketSpot?: number; marketIv?: number; marketRisk?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const [showBroker, setShowBroker] = useState(false);
  function up(field: string, value: any) { onChange({ ...leg, [field]: value }); }

  const bsGreeks = (() => {
    if (leg.type !== "option") return null;
    const S = marketSpot ?? leg.strike;
    const sigma = leg.iv_market ?? marketIv ?? 0.20;
    const r = marketRisk ?? 0.05;
    const T = daysToExpiry(leg.expiry) / 365;
    if (T <= 0 || sigma <= 0 || !leg.strike || !S) return null;
    return computeGreeks(leg.side, { S, K: leg.strike, T, r, sigma });
  })();

  return (
    <div className="leg">
      <div className="leg-h">
        <button onClick={() => setExpanded(!expanded)} style={{ color: "var(--txt-3)", fontSize: 11 }}>{expanded ? "▼" : "▶"}</button>
        <span className="swatch" style={{ background: swatchColor(leg) }} />
        <span>{TYPE_LABELS[leg.type]}</span>
        <input style={{ flex: 1, marginLeft: 6, background: "transparent", border: "none", outline: "none", fontSize: 13, color: "var(--txt)" }} value={leg.name} onChange={(e) => up("name", e.target.value)} />
        <button className="rm" onClick={onRemove}>×</button>
      </div>
      {expanded && (
        <div className="leg-b">
          <div className="two">
            <F label="Контрактов" tipText="Сколько контрактов в этой ноге (1 контракт US-опциона = 100 акций)." type="number" value={leg.quantity} onChange={(v) => up("quantity", v)} />
            {leg.type !== "cash" && leg.type !== "deposit" && leg.type !== "bond" && (
              <F label="Контракт = N" tipText="Сколько единиц актива в одном контракте (обычно 100 для US-опционов, 1 для FX/металлов)." type="number" value={leg.contract_multiplier ?? 1} onChange={(v) => up("contract_multiplier", v)} />
            )}
          </div>

          {leg.type === "option" && (
            <>
              <div className="two">
                <S label="Тип" tipText="Call растёт вверх, Put — вниз. Методика только покупает (long)." value={leg.side} options={[["call", "Call"], ["put", "Put"]]} onChange={(v) => up("side", v)} />
                <F label="Базовый актив" tipText="На что выписан опцион (тикер базового актива)." value={leg.underlying} onChange={(v) => up("underlying", v)} />
              </div>
              <div className="two">
                <F label="Цена страйка" tipText="Уровень, от которого опцион начинает приносить прибыль. OTM дешевле, но нужен бóльший ход цены." type="number" value={leg.strike} onChange={(v) => up("strike", +v)} />
                <F label="Дата экспирации" tipText="День, когда опцион «сгорает». Методика берёт длинные (≈365 дней)." type="date" value={leg.expiry} onChange={(v) => up("expiry", v)} />
              </div>
              <F label="Цена за контракт" tipText="Стоимость одного опциона (Ask). Это твой максимальный риск по этой ноге." type="number" value={leg.premium_paid} onChange={(v) => up("premium_paid", +v)}
                hint={leg.contract_multiplier && leg.quantity ? `итого ${(leg.premium_paid * leg.contract_multiplier * leg.quantity).toFixed(2)} ${leg.currency || "USD"}` : ""} />

              {bsGreeks && (
                <div className="greeks">
                  <G label="Δ" v={bsGreeks.delta} d={3} t={"Delta: изменение цены опциона при движении актива на $1. Call +, Put −, ATM ≈ ±0.5."} />
                  <G label="Γ" v={bsGreeks.gamma} d={4} t={"Gamma: скорость изменения Delta. Растёт у экспирации."} />
                  <G label="Θ" v={bsGreeks.theta} d={3} t={"Theta (в день): потеря стоимости из-за времени. Враг покупателя — выходим заранее."} />
                  <G label="V" v={bsGreeks.vega} d={3} t={"Vega (на 1% IV): чувствительность к волатильности. Рост IV после входа = прибыль."} />
                </div>
              )}

              <button className="addleg" style={{ marginTop: 10 }} onClick={() => setShowBroker((s) => !s)}>
                {showBroker ? "▲ скрыть данные терминала" : "▸ Данные из терминала (bid/ask/IV/Δ/Γ/Θ) — опционально"}
              </button>
              {showBroker && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 9 }}>
                  <div className="two">
                    <F label="Bid" type="number" value={leg.bid ?? ""} onChange={(v) => up("bid", v === "" ? null : +v)} />
                    <F label="Ask" type="number" value={leg.ask ?? ""} onChange={(v) => up("ask", v === "" ? null : +v)} />
                  </div>
                  <div className="two">
                    <F label="Last" type="number" value={leg.last ?? ""} onChange={(v) => up("last", v === "" ? null : +v)} />
                    <F label="IV (Saxo/IBKR)" type="number" value={leg.iv_market ?? ""} onChange={(v) => up("iv_market", v === "" ? null : +v)} />
                  </div>
                  <div className="two">
                    <F label="Δ Delta" type="number" value={leg.delta_market ?? ""} onChange={(v) => up("delta_market", v === "" ? null : +v)} />
                    <F label="Γ Gamma" type="number" value={leg.gamma_market ?? ""} onChange={(v) => up("gamma_market", v === "" ? null : +v)} />
                  </div>
                  <div className="two">
                    <F label="Θ Theta" type="number" value={leg.theta_market ?? ""} onChange={(v) => up("theta_market", v === "" ? null : +v)} />
                    <F label="V Vega" type="number" value={leg.vega_market ?? ""} onChange={(v) => up("vega_market", v === "" ? null : +v)} />
                  </div>
                  <F label="Open Interest" type="number" value={leg.open_interest ?? ""} onChange={(v) => up("open_interest", v === "" ? null : +v)} />
                </div>
              )}
            </>
          )}

          {leg.type === "future" && (
            <div className="two">
              <F label="Базовый актив" value={leg.underlying} onChange={(v) => up("underlying", v)} />
              <F label="Entry price" type="number" value={leg.entry_price} onChange={(v) => up("entry_price", +v)} />
            </div>
          )}

          {leg.type === "deposit" && (
            <>
              <div className="two">
                <F label="Principal" type="number" value={leg.principal} onChange={(v) => up("principal", +v)} />
                <F label="Rate" tipText="Годовая ставка подушки (доля, напр. 0.05 = 5%)." type="number" value={leg.annual_rate} onChange={(v) => up("annual_rate", +v)} />
              </div>
              <div className="two">
                <F label="Entry date" type="date" value={leg.entry_date} onChange={(v) => up("entry_date", v)} />
                <S label="Compounding" value={leg.compounding} options={[["daily", "Daily"], ["continuous", "Continuous"], ["annual", "Annual"]]} onChange={(v) => up("compounding", v)} />
              </div>
            </>
          )}

          {leg.type === "bond" && (
            <>
              <div className="two">
                <F label="Face value" type="number" value={leg.face_value} onChange={(v) => up("face_value", +v)} />
                <F label="Coupon rate" type="number" value={leg.coupon_rate} onChange={(v) => up("coupon_rate", +v)} />
              </div>
              <div className="two">
                <F label="Coupons/yr" type="number" value={leg.coupon_freq} onChange={(v) => up("coupon_freq", +v)} />
                <F label="YTM" type="number" value={leg.ytm} onChange={(v) => up("ytm", +v)} />
              </div>
              <div className="two">
                <F label="Issue date" type="date" value={leg.issue_date} onChange={(v) => up("issue_date", v)} />
                <F label="Maturity" type="date" value={leg.maturity_date} onChange={(v) => up("maturity_date", v)} />
              </div>
              <F label="Purchase price" type="number" value={leg.purchase_price} onChange={(v) => up("purchase_price", +v)} />
            </>
          )}

          {leg.type === "stock" && (
            <div className="two">
              <F label="Базовый актив" value={leg.underlying} onChange={(v) => up("underlying", v)} />
              <F label="Entry price" type="number" value={leg.entry_price} onChange={(v) => up("entry_price", +v)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = "text", hint, tipText }: { label: string; value: any; onChange: (v: any) => void; type?: string; hint?: string; tipText?: string }) {
  return (
    <label style={{ display: "block" }}>
      <div className="lbl" style={{ marginTop: 0, justifyContent: "space-between" }}>
        {tipText ? <Tip text={tipText}>{label}</Tip> : <span>{label}</span>}
        {hint && <span style={{ fontSize: 10.5, color: "var(--txt-3)" }}>{hint}</span>}
      </div>
      <input className="inp" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function G({ label, v, d, t }: { label: string; v: number; d: number; t: string }) {
  return (
    <div className="g">
      <div className="gk"><Tip text={t}>{label}</Tip></div>
      <div className="gv num">{v.toFixed(d)}</div>
    </div>
  );
}

function S({ label, value, onChange, options, tipText }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][]; tipText?: string }) {
  return (
    <label style={{ display: "block" }}>
      <div className="lbl" style={{ marginTop: 0 }}>{tipText ? <Tip text={tipText}>{label}</Tip> : <span>{label}</span>}</div>
      <select className="inp" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
